import { useRef, useState, type ReactNode } from "react";
import { Text, type ListItemOption } from "@theorgchartdev/storybook";
import type { SearchHandleRef } from "@theorgchartdev/storybook/dist/components/Search/Search";
import * as StateLib from "@/Utils/StateLib";
import {
  NormalizeRecentOptions,
  NormalizeSearchResultOptions,
  SelectSearchResult
} from "@/Utils/DataLib";
import { GetSearchResults } from "@/Utils/Promises";
import { useRightSidebarContext } from "@/providers/RightSidebarProvider";
import { checkDisconnected } from "@/appComponents/Blocks/Composites/SearchResult/SearchResult";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { useTopic } from "@/hooks/useTopic";
import { appstr } from "@coreapp/utils/appstr";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList } from "@coreapp/constants";
import type { QXDoc } from "@coreapp/CoreApp/QPlanManager";

type SearchRecord = Record<string, unknown>;
type QueryOrCriteria = string | Record<string, unknown>;
type QueryOptions = Record<string, unknown>;
type CustomResultFormatter = (
  searchResult: SearchRecord,
  doc: QXDoc,
  doDBResult: boolean,
  showHoldersAndPositionsSeparately: boolean
) => ReactNode;

export type ChartSearchOption = ListItemOption & { result?: SearchRecord };

export type ChartSearchInput = {
  queryOrCriteria: QueryOrCriteria;
  projectFields?: unknown;
  queryOptions?: QueryOptions;
  holderProject?: unknown;
};

// 'queryChartId' is ONLY used for pure DB calls used for Reports - Directory, and all other usages in app use the default handling for search, which grab open documents chart id
export type UseChartSearchParams = {
  handleSearchOptionClick?: (option: ChartSearchOption) => void;
  // Runs after the selection is handled (either path), e.g. to dismiss a host panel.
  onAfterResultSelect?: (option: ChartSearchOption) => void;
  queryChartId?: string | null;
  mockDoc?: QXDoc | null;
  projectFields?: unknown;
  onSearchResult?: (result: unknown, queryString: string) => void;
  processFullResult?: boolean;
  // Reports each finished search through onSearchResult (hosts that render their own results).
  inputOnly?: boolean;
  onResetInput?: () => void;
  defaultInput?: string;
  displayRecents?: boolean;
  queryOptions?: QueryOptions;
  recIDFieldName?: string;
  isArchive?: boolean;
  hideResultId?: unknown;
  isPositionChart?: boolean;
  getHoldersWithNoPosition?: boolean;
  handlePositions?: boolean;
  onLoading?: (isLoading: boolean) => void;
  showAssignmentStatus?: boolean;
  holderFields?: unknown;
  customResultFormatter?: CustomResultFormatter | null;
  showHoldersAndPositionsSeparately?: boolean;
  disabled?: boolean;
  mapping?: Record<string, unknown> | null;
  hideFunctionalLabels?: boolean;
};

// Chart-toolbar search scope: plan position charts also match records outside the plan.
export const getToolbarSearchQueryOptions = (doc: QXDoc | null | undefined): QueryOptions =>
  doc?.IsPositionDocument() && doc?.IsPlanChart() ? { outsidePlan: true } : {};

// Chart search engine shared by BasicChartSearchMenu (dropdown) and PhoneChartSearch (inline list).
export const useChartSearch = ({
  handleSearchOptionClick,
  onAfterResultSelect,
  queryChartId = null,
  mockDoc = null,
  projectFields,
  onSearchResult,
  processFullResult,
  inputOnly,
  onResetInput,
  defaultInput = "",
  displayRecents = true,
  queryOptions = {},
  recIDFieldName,
  isArchive = false,
  hideResultId = null,
  isPositionChart,
  getHoldersWithNoPosition = false,
  handlePositions = true,
  onLoading,
  showAssignmentStatus = false,
  holderFields,
  customResultFormatter = null,
  showHoldersAndPositionsSeparately = false,
  disabled,
  mapping = null,
  hideFunctionalLabels = false
}: UseChartSearchParams) => {
  const doc = mockDoc || StateLib.GetDoc();
  const { chartDocument } = useChartDocumentContext();
  const { highlightRecord, handleSideBarModeChange } = useRightSidebarContext();

  if (doc?.IsPlanChart()) {
    const wsMgr = StateLib.GetWSMgr();
    const planMgr = wsMgr?.GetPlanMgr();
    queryOptions.planID = planMgr?.GetPlanID();
  }

  const searchHandleRef = useRef<SearchHandleRef | null>(null);

  const [docResults, setDocResults] = useState<SearchRecord[]>([]);
  const [queryResults, setQueryResults] = useState<{ results?: SearchRecord[] }>({});
  const [queryString, setQueryString] = useState(defaultInput);
  const [loading, setLoading] = useState(false);

  const ClearSearchQuery = () => {
    searchHandleRef.current?.setValue("", true);
  };

  useTopic("ModeSwitch", () => {
    ClearSearchQuery();
    setDocResults([]);
    setQueryResults({});
  });

  const isDBQuery = !!doc?.IsServerChart() || queryChartId != null;

  const FinishSearch = (result: unknown) => {
    if (inputOnly) {
      onSearchResult?.(result, queryString);
    }
  };

  const DoDocSearch = (queryOrCriteria: QueryOrCriteria) => {
    const dbObjMatches: SearchRecord[] = [];
    if (typeof queryOrCriteria === "string") {
      if (isPositionChart) {
        doc?.DoStdStaticPositionSearch(queryOrCriteria, dbObjMatches);
      } else {
        doc?.DoStdStaticChartSearch(queryOrCriteria, dbObjMatches);
      }
    } else {
      doc?.DoAdvStaticChartSearch(queryOrCriteria, dbObjMatches);
    }
    setDocResults(dbObjMatches.slice(0));
    FinishSearch(dbObjMatches);
  };

  const handleSearchLoading = (isLoading: boolean) => {
    setLoading(isLoading);
    onLoading?.(isLoading);
  };

  const DoQuerySearch = async (input: ChartSearchInput) => {
    handleSearchLoading(true);
    const queryResult = await GetSearchResults(input.queryOrCriteria ?? queryString, {
      chartID: queryChartId,
      planID: doc?.IsPlanChart() ? StateLib.GetWSMgr()?.GetPlanMgr()?.GetPlanID() : null,
      projectFields: input.projectFields ?? projectFields,
      isArchive,
      handlePositions,
      getHoldersWithNoPosition,
      showHoldersAndPositionsSeparately,
      holderProject: input.holderProject ?? holderFields,
      ...(input.queryOptions ?? queryOptions)
    });
    handleSearchLoading(false);
    setQueryResults(queryResult.result);
    const { result } = queryResult;
    FinishSearch(processFullResult ? result : result ? result.results : []);
  };

  const onInput = async (input: ChartSearchInput) => {
    if (typeof input.queryOrCriteria === "string") {
      setQueryString(input.queryOrCriteria);
    } else {
      ClearSearchQuery();
    }

    isDBQuery ? await DoQuerySearch(input) : DoDocSearch(input.queryOrCriteria);
  };

  const onSearchResultSelect = (option: ChartSearchOption) => {
    if (handleSearchOptionClick) {
      handleSearchOptionClick(option);
    } else if (!isDBQuery && checkDisconnected(option.result)) {
      handleSideBarModeChange("staging");
      highlightRecord(option.result);
    } else {
      SelectSearchResult(option);
    }
    onAfterResultSelect?.(option);
  };

  const handleInput = (queryOrCriteria: string) => {
    onResetInput?.();
    onInput({ queryOrCriteria });
  };

  const handleResetInput = () => handleInput("");

  const searchResults: SearchRecord[] = doc
    ? ((isDBQuery ? queryResults?.results : docResults) ?? [])
    : [];
  const noResults = searchResults.length === 0;
  const noQuery = queryString.length === 0;

  // Recents first; a "no matches" row above them when a query found nothing.
  const GetPlaceholderOptions = (): ChartSearchOption[] => {
    const recentOptions: ChartSearchOption[] = displayRecents
      ? NormalizeRecentOptions(
          onSearchResultSelect,
          4,
          noResults,
          isDBQuery,
          handlePositions && isPositionChart,
          showAssignmentStatus,
          doc
        )
      : [];
    if (!noResults || noQuery) return recentOptions;

    const noMatchesOption = {
      divider: displayRecents && recentOptions.length > 0,
      disabled: true,
      value: -1,
      content: (
        <Text size={"small"} modifier={"medium"}>
          {appstr("nomatches")}
        </Text>
      )
    } as ChartSearchOption;
    return [noMatchesOption, ...recentOptions];
  };

  const allOptions: ChartSearchOption[] =
    noQuery || noResults
      ? GetPlaceholderOptions()
      : NormalizeSearchResultOptions(
          searchResults,
          onSearchResultSelect,
          undefined,
          recIDFieldName,
          isDBQuery,
          handlePositions && isPositionChart,
          showAssignmentStatus,
          customResultFormatter,
          showHoldersAndPositionsSeparately,
          mapping,
          hideFunctionalLabels,
          doc
        );
  // Excludes the record itself, so it can't be picked as its own supervisor.
  const options =
    hideResultId == null ? allOptions : allOptions.filter(o => o.value !== hideResultId);

  const searchDisabled =
    chartDocument?.state == null || !getFeatureAccess(enumFeaturesList.basicSearch) || !!disabled;

  return {
    doc,
    options,
    loading,
    queryString,
    setQueryString,
    queryResults,
    searchDisabled,
    searchHandleRef,
    onInput,
    handleInput,
    handleResetInput,
    ClearSearchQuery
  };
};
